import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-full bg-fieldiq-dark flex items-center justify-center p-8">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 max-w-md text-center">
            <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-sm text-fieldiq-text mb-4">
              {this.props.fallbackMessage || 'An unexpected error occurred. Please try refreshing the page.'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="bg-fieldiq-accent text-fieldiq-dark px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
